import { getLandingPagePath } from '@/utils/landingPage';
import { buildHexagonLeadCheckoutUrl } from '@/modules/Leads/utils/hexagonHandoff';
import { isBankOnlyQuery } from '@/modules/Leads/utils/bankOnly';
import useMiddlewareContext from './hooks/useMiddlewareContext';

export const LOGOUT_REASON_KEY = 'bambee_logout_reason';
export const LOGOUT_REASONS = {
  INACTIVE_COMPANY: 'Your company account is inactive. Please contact support.',
};

// The legacy payment page and its aliases (see nuxt.config.js router.extendRoutes).
export const PAYMENT_ROUTES = [
  '/payment',
  '/payment-offer',
  '/about-bambee/payment',
  '/about-bambee/payment-confirm',
];

export const isPaymentRoute = (path) =>
  PAYMENT_ROUTES.some(
    (route) => path === route || path.startsWith(`${route}/`)
  );

export default () => {
  const { route, redirect, store } = useMiddlewareContext();

  const paid = ['trial', 'paid', 'paying'].includes(
    store.state.current_user._company.status
  );

  // Paid users should not see payment page - redirect to app
  // Use exact match to avoid matching /settings/payment-methods
  if (
    paid &&
    (route.path === '/payment' || route.path.startsWith('/payment/'))
  ) {
    redirect(getLandingPagePath());

    return;
  }

  const companyStatus = store.state.current_user._company.status;
  const isLead = companyStatus === 'lead';

  // Whitelist check runs before the Hexagon redirect so that leads on non-payment
  // routes (/settings, /login, /terms, etc.) are not bounced to Hexagon /pay.
  // /payment is intentionally excluded: a lead landing there should go to Hexagon.
  const whitelistedRoutes = [
    '/login',
    '/settings',
    '/activate-account',
    '/terms',
    '/privacy',
    '/accounts',
    '/reset-password',
    '/insurance',
    '/qna',
    // $1 Premium People trial lead landing (BIO-1350). Its whole audience is inactive/no-plan
    // companies, so the non-paid redirect (owners -> /payment, others -> logout) must not fire here.
    '/trial/premium-people',
  ];

  const allowed = whitelistedRoutes.some((white) => route.path.includes(white));

  if (allowed || paid) {
    return;
  }

  // Leads on /payment (or any other non-whitelisted route) go to Hexagon /pay (BIO-2258).
  // The legacy payment link carries ?ach=true (and the app's own links use ?ach=1), so read the
  // flag the same way the payment page does to keep the bank-only flow intact.
  if (isLead) {
    const ach = isBankOnlyQuery(route.query);
    const hexagonUrl = buildHexagonLeadCheckoutUrl(ach);
    if (hexagonUrl) {
      redirect(hexagonUrl);

      return;
    }
    // HEXAGON_URL not configured (e.g. local dev without the var) - fall through
  }

  // Already on the payment page (or one of its aliases): redirecting to /payment from here is a
  // no-op that vue-router rejects with NavigationDuplicated, which leaves a blank page. Stay put,
  // as the old whitelist (which listed /payment) did.
  if (isPaymentRoute(route.path)) {
    return;
  }

  // Unpaid users (inactive company) handling
  // Owners/admins (role='user') go to /payment, non-owners get logged out
  const userRole = store.state.current_user.role;
  const isOwnerOrAdmin = userRole === 'user';

  const isInactiveOwnerOrAdmin = companyStatus === 'inactive' && isOwnerOrAdmin;
  const isInactiveNonOwner = companyStatus === 'inactive' && !isOwnerOrAdmin;

  // Non-owners of inactive companies get logged out with an error message
  if (isInactiveNonOwner) {
    // Store reason in localStorage (persists through logout which clears sessionStorage)
    if (typeof window !== 'undefined') {
      localStorage.setItem(LOGOUT_REASON_KEY, LOGOUT_REASONS.INACTIVE_COMPANY);
    }
    store.dispatch('logout');

    return;
  }

  if (isLead || isInactiveOwnerOrAdmin) {
    redirect(`/payment`);
  }
};
